import { JsonPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  OnInit,
  signal,
  untracked,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { MenuItem } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { TagModule } from 'primeng/tag';
import { TableLazyLoadEvent } from 'primeng/table';
import { filter } from 'rxjs/operators';
import { CesCardCarouselComponent } from '../../../../ui/ces-card-carousel';
import { ConfirmationHelperService } from '../../../../../services/confirmation-helper.service';
import { PaginatedUrlSyncService, ParsedFilters } from '../../../../../services/paginated-url-sync.service';
import { PermissionFacadeService } from '../../../../../services/permission-facade.service';
import { TestModeService } from '../../../../../services/test-mode.service';
import { ToastService } from '../../../../../services/toast.service';
import { InfoStore } from '../../../../../stores/info.store';
import { MeasureTypeStore } from '../../../../../stores/measure-type.store';
import { Campaign, CampaignStatus } from '../../../../../types/campaign.interface';
import { CampaignUpdatePayload, WebSocketMessageType } from '../../../../../types/websocket.types';
import { registerWsUpdateRefresh } from '../../../../../utils/measurement-update-refresh';
import { campaignUpdateTargetsAsset } from '../../../../../utils/websocket-match';
import {
  CAMPAIGN_STATUS_I18N_KEYS,
  CAMPAIGN_STATUS_SEVERITY,
  formatCampaignDateTime,
  getCampaignStatusLabel,
} from '../../../../../types/campaign.utils';
import { Severity } from '../../../../../types/general-types';
import { LazyLoadConverter } from '../../../../../utils/lazy-load.converter';
import { GenericParentGridComponent } from '../../../../generic/generic-parent-grid.component';
import { CesAssetBreadcrumbComponent } from '../../../../ui/ces-asset-breadcrumb/ces-asset-breadcrumb.component';
import { CesButtonComponent } from '../../../../ui/ces-buttons';
import { CesButtonSecondaryComponent } from '../../../../ui/ces-buttons/ces-button-secondary.component';
import { CesButtonTertiaryComponent } from '../../../../ui/ces-buttons/ces-button-tertiary.component';
import { CesButtonsModule } from '../../../../ui/ces-buttons/ces-buttons.module';
import { CesMenuButtonSecondaryComponent } from '../../../../ui/ces-buttons/ces-menu-button-secondary.component';
import { CesCampaignTagsComponent } from '../../../../ui/ces-campaign-tags/ces-campaign-tags.component';
import { CesCard } from '../../../../ui/ces-card/ces-card.component';
import { CesCheckableMenuComponent } from '../../../../ui/ces-checkable-menu/ces-checkable-menu.component';
import { CesTitleComponent } from '../../../../ui/ces-route-title/ces-route-title.component';
import { CesCampaignListSkeletonComponent } from '../../../../ui/skeletons/campaign-list.skeleton.component';
import { CesGridSkeletonComponent } from '../../../../ui/skeletons/grid-skeleton.component';
import { CampaignActionsService } from './sub-services/campaign-actions.service';
import { CampaignCreationService } from './sub-services/campaign-creation.service';
import { CampaignDataService } from './sub-services/campaign-data.service';
import { CampaignGridConfigService } from './sub-services/campaign-grid-config.service';
import { CampaignHistoryService } from './sub-services/campaign-history.service';
import { CampaignLoadingService } from './sub-services/campaign-loading.service';
import { CampaignNavigationService } from './sub-services/campaign-navigation.service';
import { CompletedCampaignsGridStore } from './sub-services/completed-campaigns-grid.store';
import { formatDate } from '../../../../../utils/date.utils';

const CAMPAIGN_FILTER_FIELDS = ['timestamp', 'operator', 'comment'];
const CLOSED_CAMPAIGNS_URL_RE = /\/monitoring\/campaigns(?:\?|$|\/$)/;

/** A remote campaign update reloads the campaigns page only for the currently-loaded asset,
 *  and never while a grid lazy-load is already in flight. */
export function shouldReloadCampaignsOnUpdate(
  assetId: number | null,
  lastLoadedAssetId: number | null,
  isLoadingFromGrid: boolean,
): assetId is number {
  return assetId != null && assetId === lastLoadedAssetId && !isLoadingFromGrid;
}

@Component({
  selector: 'ces-campaigns',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ButtonModule,
    CesAssetBreadcrumbComponent,
    CesCardCarouselComponent,
    CesButtonSecondaryComponent,
    CesButtonsModule,
    CesButtonComponent,
    CesButtonTertiaryComponent,
    CesCampaignListSkeletonComponent,
    CesGridSkeletonComponent,
    CesCampaignTagsComponent,
    CesCard,
    CesCheckableMenuComponent,
    CesMenuButtonSecondaryComponent,
    CesTitleComponent,
    CheckboxModule,
    FormsModule,
    GenericParentGridComponent,
    JsonPipe,
    TagModule,
    TranslocoModule,
  ],
  templateUrl: './campaigns-list.component.html',
  styleUrl: './campaigns-list.component.scss',
})
export class CesCampaignsListComponent implements OnInit {
  readonly testMode = inject(TestModeService);
  private readonly permissionFacade = inject(PermissionFacadeService);

  readonly nav = inject(CampaignNavigationService);
  private readonly actions = inject(CampaignActionsService);
  readonly data = inject(CampaignDataService);
  readonly loading = inject(CampaignLoadingService);
  readonly gridStore = inject(CompletedCampaignsGridStore);
  readonly gridConfig = inject(CampaignGridConfigService);
  readonly creation = inject(CampaignCreationService);
  private readonly historyService = inject(CampaignHistoryService);

  private readonly destroyRef = inject(DestroyRef);
  private readonly confirmationHelper = inject(ConfirmationHelperService);
  private readonly measureTypeStore = inject(MeasureTypeStore);
  private readonly infoStore = inject(InfoStore);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly router = inject(Router);
  private readonly translate = inject(TranslocoService);
  private readonly route = inject(ActivatedRoute);
  private readonly toastService = inject(ToastService);
  private readonly paginatedUrlSync = inject(PaginatedUrlSyncService);

  private readonly campaignMenuItemsCache = signal<Map<number, { status: CampaignStatus; items: MenuItem[] }>>(new Map());

  @ViewChild('columnMenu') private readonly columnMenu!: CesCheckableMenuComponent;

  readonly currentAssetId = this.nav.currentAssetId;
  readonly canViewCampaigns = this.permissionFacade.hasReadAccess('monitoring/campaigns');

  readonly tableDefinition = this.gridConfig.tableDefinition;
  readonly campaignsWithPuces = this.gridStore.campaignsWithPuces;
  readonly totalRecords = this.gridStore.totalRecords;
  readonly first = this.gridStore.first;
  readonly rows = this.gridStore.rows;
  readonly totalCampaignsCount = computed(() => this.data.currentOpenCampaigns().length + this.totalRecords());

  readonly urlFilters = signal<ParsedFilters | undefined>(
    this.paginatedUrlSync.parseFiltersFromUrl(this.route.snapshot.queryParams, this.gridConfig.tableDefinition()),
  );

  readonly urlSort = signal<{ field: string | null; order: number }>(
    (() => {
      const params = this.route.snapshot.queryParams;
      const rawSortField: unknown = params['sortField'];
      const rawSortOrder: unknown = params['sortOrder'];
      const sortField = typeof rawSortField === 'string' ? rawSortField : null;
      const sortOrder = rawSortOrder === 'desc' ? -1 : rawSortOrder === 'asc' ? 1 : null;
      return { field: sortField, order: sortOrder ?? -1 };
    })(),
  );

  readonly tableDefinitionWithSort = computed(() => {
    const def = this.tableDefinition();
    const sort = this.urlSort();
    if (sort.field) {
      return { ...def, defaultSortField: sort.field, defaultSortOrder: sort.order as 1 | -1 | 0 };
    }
    return def;
  });

  private readonly currentQueryParams = toSignal(this.route.queryParamMap);
  readonly hasActiveFilters = computed(() => {
    // The `filters` query param is the custom `field:value` format produced by
    // PaginatedUrlSyncService, not JSON — parse it with the same service so an
    // active filter that yields zero rows still keeps the grid (and its filter
    // row) visible instead of collapsing to the "no campaigns" empty state.
    const raw = this.currentQueryParams()?.get('filters');
    if (!raw) return false;
    const parsed = this.paginatedUrlSync.parseFiltersFromUrl({ filters: raw }, this.tableDefinition());
    return !!parsed && Object.keys(parsed).length > 0;
  });

  readonly openCampaigns = computed(() =>
    this.data
      .currentOpenCampaigns()
      .sort((a: Campaign, b: Campaign) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()),
  );

  readonly openCampaignsWithMenuItems = computed(() => {
    const campaigns = this.openCampaigns();
    const cache = this.campaignMenuItemsCache();
    return campaigns.map((campaign: Campaign) => {
      const cached = cache.get(campaign.id);
      const menuItems = cached?.status === campaign.status ? cached.items : [];
      return { ...campaign, menuItems };
    });
  });

  private lastLoadedAssetId: number | null = null;
  private isLoadingFromGrid = false;
  private skipNextNavigationLoad = true;
  private skipNextLazyLoad = true;

  constructor() {
    // Live: a CAMPAIGN_UPDATE for this asset reloads both the open and completed campaign
    // grids so status / pending-conflict / imported changes from other operators appear
    // without a manual refresh. Menu items re-derive via the status-diff effect below.
    const wsRefresh = registerWsUpdateRefresh<CampaignUpdatePayload>(WebSocketMessageType.CAMPAIGN_UPDATE, {
      targets: payload => campaignUpdateTargetsAsset(payload, this.currentAssetId()),
      refresh: () => this.onRemoteCampaignUpdate(),
      debounceMs: 500,
    });
    // Reconcile after a socket outage; the assetId===lastLoadedAssetId guard makes any
    // pre-load reconnect tick a no-op.
    wsRefresh.startReconnectCatchup();

    effect(() => {
      const campaigns = this.openCampaigns();
      untracked(() => {
        const cache = this.campaignMenuItemsCache();
        campaigns.forEach((campaign: Campaign) => {
          const cached = cache.get(campaign.id);
          if (cached?.status !== campaign.status) {
            queueMicrotask(() => {
              void this.loadMenuItemsForCampaign(campaign);
            });
          }
        });
      });
    });
  }

  ngOnInit(): void {
    this.route.parent?.parent?.params.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      const assetId = Number(params['id']);
      if (assetId > 0 && assetId !== this.lastLoadedAssetId) {
        this.lastLoadedAssetId = assetId;
        void this.loadAssetData(assetId);
      }
    });

    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        filter(event => CLOSED_CAMPAIGNS_URL_RE.test(event.urlAfterRedirects)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        if (this.skipNextNavigationLoad) {
          this.skipNextNavigationLoad = false;
          return;
        }
        if (this.isLoadingFromGrid) return;
        const assetId = this.currentAssetId();
        if (assetId && assetId === this.lastLoadedAssetId) {
          void this.reloadFromUrl(assetId);
        }
      });

    this.translate
      .selectTranslation()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.campaignMenuItemsCache.set(new Map());
        this.cdr.markForCheck();
      });

    this.historyService.listMutations$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      const assetId = this.currentAssetId();
      if (assetId && assetId === this.lastLoadedAssetId) {
        void this.reloadFromUrl(assetId);
      }
    });
  }

  async loadAssetData(assetId: number): Promise<void> {
    try {
      this.loading.reset();
      this.gridStore.clearPagination();
      await Promise.all([
        this.data.loadCampaignsByAsset(assetId),
        this.measureTypeStore.getByAssetId(assetId),
        this.infoStore.getByAssetId(assetId),
        this.data.manualMeasureStore.getByAssetId(assetId),
        this.creation.loadCampaignModels(assetId),
        this.loading.executeWithLoading(() => this.reloadFromUrl(assetId), 'campaignData'),
      ]);
    } catch {
      this.toastService.error('entities.campaign.messages.loadError');
    }
  }

  private onRemoteCampaignUpdate(): void {
    const assetId = this.currentAssetId();
    if (!shouldReloadCampaignsOnUpdate(assetId, this.lastLoadedAssetId, this.isLoadingFromGrid)) return;
    void this.reloadAll(assetId);
  }

  private async reloadAll(assetId: number): Promise<void> {
    await Promise.all([this.data.loadCampaignsByAsset(assetId), this.reloadFromUrl(assetId)]);
  }

  private async reloadFromUrl(assetId: number): Promise<void> {
    const { queryParams, toSync } = this.paginatedUrlSync.resolveState(
      this.route,
      this.paginatedUrlSync.buildStateKey('campaigns-grid', assetId),
    );
    const tableEvent = this.paginatedUrlSync.buildTableEventFromUrl(queryParams, this.tableDefinition());
    this.urlFilters.set(tableEvent.filters);
    this.urlSort.set({ field: tableEvent.sortField || null, order: tableEvent.sortOrder });

    if (toSync) {
      this.isLoadingFromGrid = true;
      this.paginatedUrlSync.syncUrlInBackground(this.router, this.route, toSync, () => (this.isLoadingFromGrid = false));
    }

    const params = LazyLoadConverter.fromTableEvent(tableEvent, CAMPAIGN_FILTER_FIELDS);
    await this.gridStore.loadPaginatedByAssetId(assetId, params);
  }

  async onLazyLoad(event: TableLazyLoadEvent): Promise<void> {
    const assetId = this.currentAssetId();
    if (!assetId) return;
    if (this.skipNextLazyLoad) {
      this.skipNextLazyLoad = false;
      return;
    }

    this.isLoadingFromGrid = true;
    try {
      const rawSortField = Array.isArray(event.sortField) ? event.sortField[0] : event.sortField;
      this.urlSort.set({ field: rawSortField ?? null, order: event.sortOrder ?? -1 });

      const queryParams = this.paginatedUrlSync.buildQueryParamsFromEvent(event, this.tableDefinition());
      this.paginatedUrlSync.saveState(this.paginatedUrlSync.buildStateKey('campaigns-grid', assetId), queryParams);
      await this.paginatedUrlSync.navigateWithParams(this.router, this.route, queryParams);

      const params = LazyLoadConverter.fromTableEvent(event, CAMPAIGN_FILTER_FIELDS);
      await this.gridStore.loadPaginatedByAssetId(assetId, params);
    } finally {
      this.isLoadingFromGrid = false;
    }
  }

  async onCustomCampaignAction(action: string, campaign: Campaign): Promise<void> {
    if (action !== 'reopen') return;
    const confirmed = await this.confirmationHelper.ask(
      'common.messages.confirmAction',
      'entities.campaign.messages.reopenTitle',
      undefined,
      'pi pi-exclamation-circle',
      'common.buttons.reopen',
    );
    if (!confirmed) return;
    await this.updateCampaignStatus(campaign.id, CampaignStatus.ON_GOING);
    this.toastService.success('entities.campaign.messages.reopenSuccess');
  }

  async updateCampaignStatus(campaignId: number, status: CampaignStatus): Promise<void> {
    try {
      await this.historyService.updateCampaignStatusWithHistory(campaignId, status);
      this.toastService.success('entities.campaign.messages.statusUpdated', undefined, undefined, undefined, {
        status: this.getStatusLabel(status),
      });
    } catch {
      this.toastService.error('common.toasts.operationFailed');
    }
  }

  async createNewCampaign(): Promise<void> {
    const newCampaign = await this.creation.createCampaign();
    if (newCampaign) {
      this.navigateToCampaign(newCampaign.id);
    }
  }

  private async loadMenuItemsForCampaign(campaign: Campaign): Promise<void> {
    let items: MenuItem[] = [];
    try {
      items = await this.nav.getFilteredCampaignMenuItems(campaign);
    } catch {}

    this.campaignMenuItemsCache.update(cache => {
      const newCache = new Map(cache);
      newCache.set(campaign.id, { status: campaign.status, items });
      return newCache;
    });
  }

  navigateToCampaign(campaignId: number): void {
    const assetId = this.currentAssetId();
    if (assetId) {
      void this.router.navigate(['/assets', assetId, 'monitoring', 'campaigns', campaignId]);
    }
  }

  // Real href so the card's overlay anchor offers native open-in-new-tab; plain click still routes via cardClick.
  campaignHref(campaign: Campaign): string | null {
    const assetId = this.currentAssetId();
    return assetId ? `/assets/${assetId}/monitoring/campaigns/${campaign.id}` : null;
  }

  toggleColumnSelector(event: Event): void {
    this.columnMenu?.toggle(event);
  }

  checkAllColumns(): void {
    this.gridConfig.checkAllColumns();
  }

  uncheckAllColumns(): void {
    this.gridConfig.uncheckAllColumns();
  }

  campaignTitle(campaign: Campaign): string {
    const date = formatDate(new Date(campaign.timestamp));
    const prefix = this.testMode.testMode() ? `#${campaign.id} ` : '';
    return `${prefix}${date}`;
  }

  formatCampaignDate(campaign: Campaign): string {
    return formatCampaignDateTime(campaign);
  }

  handlePuceRecalculation(event: { campaignId: number; measureTypeId: number }): void {
    this.gridStore.recalculatePuce(event.campaignId, event.measureTypeId);
  }

  getStatusKey(status: CampaignStatus): string {
    return CAMPAIGN_STATUS_I18N_KEYS[status] || CAMPAIGN_STATUS_I18N_KEYS[CampaignStatus.UNKNOWN];
  }

  getStatusLabel(status: CampaignStatus): string {
    return getCampaignStatusLabel(status, this.translate);
  }

  getTagSeverity(status: CampaignStatus): Severity {
    return CAMPAIGN_STATUS_SEVERITY[status] ?? 'info';
  }

  onResolveConflicts(campaign: Campaign): void {
    void this.actions.resolveImportConflicts(campaign);
  }
}
